import { useState } from "react";
import { AlertTriangle, Download, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { downloadFoundationBudgetCsv } from "@/lib/api";

// Foundation rejects a budget import once the job has a completion date.
// The push was refused (409) because this job's budget was never exported.
// Two choices, both logged — the date is never sent silently.
export function BudgetBeforeCompletionDialog({ block, woId, busy, onClose, onPushWithout, onExported }) {
  const [exporting, setExporting] = useState(false);
  if (!block) return null;
  const gate = block.gate || {};

  const exportNow = async () => {
    setExporting(true);
    try {
      const res = await downloadFoundationBudgetCsv([woId], "before_completion_push");
      const filename = res.headers?.["x-budget-export-filename"] || "Foundation_Budget.csv";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`${filename} downloaded — budget export logged; pushing with the completion date`);
      onExported?.();
    } catch (e) {
      let msg = "Budget export failed";
      try { msg = JSON.parse(await e?.response?.data?.text?.())?.detail || msg; } catch { /* not json */ }
      toast.error(msg);
    } finally { setExporting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="budget-completion-block-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-900"><AlertTriangle className="w-4 h-4" /> Budget Not Exported</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-700" data-testid="budget-completion-block-message">{block.message}</p>
        <div className="text-xs text-slate-500">
          Completion date on this work order: <strong>{gate.completion_date}</strong>.
          {gate.can_export_now
            ? " The job already exists in Foundation, so the budget CSV can be exported now and the push then continues with the completion date."
            : " This job has not been pushed yet — Foundation needs the job before a budget can be imported, so push without the date first, export the budget, then push again."}
        </div>
        <div className="flex flex-wrap gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="h-8 px-3 text-xs rounded-md border border-slate-300 text-slate-700 bg-white hover:bg-slate-50" data-testid="budget-completion-cancel">Cancel</button>
          <button type="button" onClick={onPushWithout} disabled={busy || exporting}
                  className="h-8 px-3 text-xs rounded-md border border-slate-400 text-slate-800 bg-white hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="budget-completion-push-without">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Push without the completion date
          </button>
          {gate.can_export_now && (
            <button type="button" onClick={exportNow} disabled={busy || exporting}
                    className="h-8 px-3 text-xs rounded-md bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="budget-completion-export-now">
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export now, then push
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
